
import React, { useCallback, useMemo, useRef, useState } from "react";
import cx from "classnames";
import { debounce, flatMap, get, map, times } from "lodash";
import { Row } from "react-bootstrap";
import { useSelector } from "react-redux";

import InfiniteScroll from "src/components/infiniteScroll";
import Slider from "src/components/slider";
import Button from "src/components/button";
import Text from "src/components/text";

import { useAddStoryboardUsersMutation } from "src/query/experiences/storyboardSettings/permissions";
import { AllUserRecord } from "src/types/experiences/workspaceSetting";
import { toastSuccess, toastFailure } from "../../utils/notifications";
import PlaceholderCell from "../rolesAndPermissions/components/placeholderCell";

import { useAllWorkspaceUsersQuery } from "src/query/experiences/workspaceSettings/userManagement";
import { InviteUsersDialogProps, UserSelection } from "src/types/experiences/storyboardSettings";
import { DropDown } from "../../../components/dropdown";
import { Avatar } from "../rolesAndPermissions/components/avatar";
import { ROLE_OPTIONS, inviteUserHeader } from "src/pages/experiences/constants/storyboard";
import styles from "./inviteUsersDialog.module.scss";

const UserRowPlaceholder = () => (
  <Row className={styles.userRow}>
    {map(inviteUserHeader, (header) => (
      <PlaceholderCell
        key={header.label}
        col={header.width}
        type={header.value}
      />
    ))}
  </Row>
);
// ---------------------------------------------------------------------------
// Single user row
// ---------------------------------------------------------------------------

const UserRow = ({
  user,
  selection,
  onRoleChange,
}: {
  user: AllUserRecord;
  selection: UserSelection | undefined;
  onRoleChange: (userId: string, role: string) => void;
}) => {
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
  const hasRole = Boolean(selection?.role);

  return (
    <div className={styles.userRow}>
      <Avatar user={user} />

      <div className={styles.userInfo}>
        {fullName && (
          <Text text={fullName} lines={1} tooltip={{ placement: "top" }} className={styles.userName} />
        )}
        <Text text={user.email} lines={1} tooltip={{ placement: "top" }} className={styles.userEmail} />
      </div>

      <div className={styles.roleWrapper}>
        <DropDown
            key={selection?.role ?? "unset"}
            selected={{
              key: selection?.role ?? "",
              label: hasRole
                ? (ROLE_OPTIONS.find((o) => o.key === selection!.role)?.label ?? "")
                : "",
            }}
            placeholder="Select Role"
            options={ROLE_OPTIONS}
            onChange={(key) => onRoleChange(user.id, key)}
          />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main dialog
// ---------------------------------------------------------------------------

const InviteUsersDialog: React.FC<InviteUsersDialogProps> = ({
  show,
  storyboardId,
  workspaceId,
  onClose,
  onSuccess,
}) => {
  const currentUser = useSelector((state: any) => get(state, "users.me"));

  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [selections, setSelections] = useState<Record<string, UserSelection>>({});

  const { mutate: addUsers, isPending: isAdding } = useAddStoryboardUsersMutation();

  const { data, isFetching, hasNextPage, loadMore } = useAllWorkspaceUsersQuery(
    workspaceId,
    { query: debouncedQuery },
    show,
  );

  const allUsers: AllUserRecord[] = useMemo(
    () => flatMap(data?.pages ?? [], (page) => page.records),
    [data],
  );

  const filteredUsers = useMemo(
    () => allUsers.filter((u) => u.enabled && u.id !== currentUser?.id),
    [allUsers, currentUser],
  );

  const debouncedSearch = useRef(
    debounce((q: string) => setDebouncedQuery(q), 300),
  ).current;

  const handleQueryChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setQuery(e.target.value);
      debouncedSearch(e.target.value);
    },
    [debouncedSearch],
  );

  const handleClearQuery = () => {
    setQuery("");
    setDebouncedQuery("");
  };

  const handleRoleChange = useCallback((userId: string, role: string) => {
    setSelections((prev) => ({ ...prev, [userId]: { role } }));
  }, []);

  const handleClose = () => {
    if (isAdding) return;
    setQuery("");
    setDebouncedQuery("");
    setSelections({});
    onClose();
  };

  const handleConfirm = () => {
    const users = Object.entries(selections)
      .filter(([, { role }]) => Boolean(role))
      .map(([userId, { role }]) => ({
        user: userId,
        role,
      }));
  
    if (!users.length) return;
  
    addUsers(
      {
        storyboardId,
        data: users,
      },
      {
        onSuccess: () => {
          toastSuccess("Users added to storyboard");
          setSelections({});
          setQuery("");
          setDebouncedQuery("");
          onSuccess();
        },
        onError: (error) => {
          console.error(error);
          toastFailure("Failed to add users");
        },
      },
    );
  };

  const assignedCount = Object.values(selections).filter((s) => s.role).length;

  return (
    <Slider.Dialog width="400" show={show} onClose={handleClose} className={styles.inviteSlider}>
      <Slider.Header
        title="add users to storyboard"
        message="Your chance to offer seats on this rocket ship"
      />

      <Slider.Body>
        {/* Search */}
        <div className={styles.searchBar}>
          <i className={cx("paperflite-search", styles.searchIcon)} />
          <input
            className={styles.searchInput}
            placeholder="Type here to search..."
            value={query}
            onChange={handleQueryChange}
            disabled={isAdding}
            autoFocus
          />
          {query && (
            <button className={styles.clearBtn} onClick={handleClearQuery}>
              <i className="paperflite-close" />
            </button>
          )}
        </div>
        

        {/* User list */}
        <div className={styles.listWrapper}>
        {isFetching && filteredUsers.length === 0 ? (
          <div className={styles.scrollable}>
            {times(6, (index) => (
              <UserRowPlaceholder key={index} />
            ))}
          </div>
        ) : filteredUsers.length === 0 ? (
            <div className={styles.emptyState}>
              <span>No users found</span>
            </div>
          ) : (
            <InfiniteScroll
              loading={isFetching}
              hasMore={hasNextPage ?? false}
              loadMore={loadMore}
              className={styles.scrollable}
              loader={() => (
                <>
                  <UserRowPlaceholder />
                  <UserRowPlaceholder />
                </>
              )}
            >
              {map(filteredUsers, (user) => (
                <UserRow
                  key={user.id}
                  user={user}
                  selection={selections[user.id]}
                  onRoleChange={handleRoleChange}
                />
              ))}
            </InfiniteScroll>
          )}
        </div>
      </Slider.Body>

      <Slider.Actions>
        <Button
          variant="primary"
          icon="paperflite-tick"
          text={isAdding ? "Adding…" : "Confirm"}
          disabled={isAdding || assignedCount === 0}
          onClick={handleConfirm}
        />
        <Button
          variant="secondary"
          text="Cancel"
          disabled={isAdding}
          onClick={handleClose}
        />
      </Slider.Actions>
    </Slider.Dialog>
  );
};

export default InviteUsersDialog;